import { createContext, useContext } from 'react';
import type React from 'react';
import useStickyState from 'packages/signup/components/useStickyState';
import useLeadSource from 'packages/signup/components/useLeadSource';
import { trackQuestionAnswered } from '../analytics/tracking_helpers';
import type { PartnerQuestionType, SignUpGoalsValues, PerkGoalsValues, YesNoValues } from './types';
import { PartnerQuestionKeys } from './types';

export type ContextContents = PartnerQuestionType & {
  leadSource: string;
  tierSource: string;
  setSignUpGoalsComplete: (answer: [SignUpGoalsValues]) => void;
  setRunPayrollForClientsComplete: (answer: YesNoValues) => void;
  setOfferAdvisoryServicesComplete: (answer: YesNoValues) => void;
  setPerkGoalsComplete: (answer: [PerkGoalsValues]) => void;
};

type PartnerSignupProviderProps = {
  readonly children: React.ReactNode;
};

export const PartnerSignupContext = createContext<ContextContents>({
  setSignUpGoalsComplete: () => {},
  setRunPayrollForClientsComplete: () => {},
  setOfferAdvisoryServicesComplete: () => {},
  setPerkGoalsComplete: () => {},
  leadSource: '',
  tierSource: '',
});

export const PartnerSignupProvider: React.FC<PartnerSignupProviderProps> = ({ children }) => {
  const { leadSource, tierSource } = useLeadSource();

  const [signupGoals, setSignupGoals] = useStickyState<[SignUpGoalsValues] | undefined>(
    undefined,
    PartnerQuestionKeys.SIGNUP_GOALS
  );

  const [runPayrollForClients, setRunPayrollForClients] = useStickyState<YesNoValues | undefined>(
    undefined,
    PartnerQuestionKeys.RUN_PAYROLL_FOR_CLIENTS
  );

  const [offerAdvisoryServices, setOfferAdvisoryServices] = useStickyState<YesNoValues | undefined>(
    undefined,
    PartnerQuestionKeys.OFFER_ADVISORY_SERVICES
  );

  const [perkGoals, setPerkGoals] = useStickyState<[PerkGoalsValues] | undefined>(
    undefined,
    PartnerQuestionKeys.PERK_GOALS
  );

  const setSignUpGoalsComplete = (answer: [SignUpGoalsValues]) => {
    trackQuestionAnswered(PartnerQuestionKeys.SIGNUP_GOALS, answer.join(', '), leadSource);
    setSignupGoals(answer);
  };
  const setRunPayrollForClientsComplete = (answer: YesNoValues) => {
    trackQuestionAnswered(PartnerQuestionKeys.RUN_PAYROLL_FOR_CLIENTS, answer, leadSource);
    setRunPayrollForClients(answer);
  };
  const setOfferAdvisoryServicesComplete = (answer: YesNoValues) => {
    trackQuestionAnswered(PartnerQuestionKeys.OFFER_ADVISORY_SERVICES, answer, leadSource);
    setOfferAdvisoryServices(answer);
  };
  const setPerkGoalsComplete = (answer: [PerkGoalsValues]) => {
    trackQuestionAnswered(PartnerQuestionKeys.PERK_GOALS, answer.join(', '), leadSource);
    setPerkGoals(answer);
  };

  const providerValues = {
    signupGoals,
    setSignUpGoalsComplete,
    runPayrollForClients,
    setRunPayrollForClientsComplete,
    offerAdvisoryServices,
    setOfferAdvisoryServicesComplete,
    perkGoals,
    setPerkGoalsComplete,
    leadSource,
    tierSource,
  };

  return (
    <PartnerSignupContext.Provider value={providerValues}>{children}</PartnerSignupContext.Provider>
  );
};

export function usePartnerSignupContext() {
  return useContext(PartnerSignupContext);
}
